import { useEffect, useState } from 'react';

import { translation, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import Button, { ButtonKind } from 'bloko/blocks/button';
import { H2 } from 'bloko/blocks/header';
import Link from 'bloko/blocks/link';
import Loading, { LoadingScale } from 'bloko/blocks/loading';
import Text, { TextImportance, TextSize } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';
import urlParser from 'bloko/common/urlParser';

import useIsClient from 'src/hooks/useIsClient';
import PostMessage from 'src/utils/PostMessage';

import AttachResume from 'src/pages/ApplicantServicesOrder/components/CareerGuidance/AttachResume';
import Reset from 'src/pages/ApplicantServicesOrder/components/CareerGuidance/Reset';

import styles from './styles.less';

const TrlKeys = {
    download: 'applicantServices.career_guidance.result.download',
    results: 'applicantServices.career_guidance.title.results',
    sharedDescription: 'applicantServices.career_guidance.title.sharedDescription',
    sharedDescriptionLink: 'applicantServices.career_guidance.title.sharedDescription.link',
    noResults: 'applicantServices.career_guidance.noResults',
    noResultsLink: 'applicantServices.career_guidance.noResults.link',
    loading: 'applicantServices.career_guidance.loading',
};

const CareerGuidance: TranslatedComponent = ({ trls }) => {
    const state = useSelectorNonNullable(({ careerGuidanceState }) => careerGuidanceState);
    const frameUrl = state.resultsUrl || state.entryUrl;
    const [iframeHeight, setIframeHeight] = useState<string>('auto');
    const isXs = useBreakpoint() === Breakpoint.XS;
    const isClient = useIsClient();

    useEffect(() => {
        if (!frameUrl) {
            return () => null;
        }
        return PostMessage.on({
            origin: urlParser(frameUrl).host,
            handlers: {
                'htline:career_guidance:frame:sizes': (data: { height: number }) => {
                    setIframeHeight(`${data.height}px`);
                },
                'htline:career_guidance:frame:testDone': () => {
                    location.reload();
                },
            },
        });
    }, [frameUrl]);
    return (
        <>
            {state.resultsReceived && (
                <>
                    <H2>{trls[TrlKeys.results]}</H2>
                    <VSpacing base={4} />
                </>
            )}
            {state.isOwner && state.resultsReceived && (
                <>
                    <div className={styles.buttonsContainer}>
                        <AttachResume resumes={state.resumes} stretched={isXs} />
                        {state.pdfPath && (
                            <Button
                                stretched={isXs}
                                kind={ButtonKind.Success}
                                Element="a"
                                href={state.pdfPath}
                                data-qa="career-guidance-download"
                                target="_blank"
                            >
                                {trls[TrlKeys.download]}
                            </Button>
                        )}
                        <Reset pdfPath={state.pdfPath} stretched={isXs} />
                    </div>
                </>
            )}
            {!state.isOwner && (
                <>
                    <Text size={TextSize.Small} importance={TextImportance.Secondary}>
                        {formatToReactComponent(trls[TrlKeys.sharedDescription], {
                            '{0}': (
                                <Link disableVisited href="/article/proforientation_promo" target="_blank">
                                    {trls[TrlKeys.sharedDescriptionLink]}
                                </Link>
                            ),
                        })}
                    </Text>
                </>
            )}
            <VSpacing base={4} />
            {frameUrl && (
                <div className={styles.iframeContainer}>
                    {isClient ? (
                        <iframe
                            src={frameUrl}
                            style={{ height: iframeHeight }}
                            className={styles.iframe}
                            data-qa="proforientation-iframe"
                        />
                    ) : (
                        <>
                            <Text size={TextSize.Large}>{trls[TrlKeys.loading]}</Text>
                            <Loading scale={LoadingScale.Medium} />
                        </>
                    )}
                </div>
            )}
            {!frameUrl && (
                <Text>
                    {formatToReactComponent(trls[TrlKeys.noResults], {
                        '{0}': (
                            <Link disableVisited href="">
                                {trls[TrlKeys.noResultsLink]}
                            </Link>
                        ),
                    })}
                </Text>
            )}
        </>
    );
};

export default translation(CareerGuidance);
